/**
 * matrixRowLookup.js — resolve a response record to its MatrixQuestionRows def
 *
 * Every MSI analytics surface needs the same two answers about a response:
 * "is this matrix row negatively worded (so the score must be mirrored)?" and
 * "which row does it belong to (so answers bucket together)?".
 *
 * Both used to be answered by matching `QuestionnairesResponses.RowTitle`
 * against `MatrixQuestionRows.Title`. That join is unsound: RowTitle is frozen
 * at answer time, so rewording a row strands every earlier answer — it misses
 * the posNeg lookup (a negative row then scores as positive) and lands in its
 * own bucket, so first/last moments never pair. In the other direction, an
 * unrelated row elsewhere that happens to share the title gets pulled in.
 * `RowID` is the stable link; only docs written before RowID existed need the
 * title.
 *
 * Only the exact string 'negative' mirrors — MatrixQuestionRows.posNeg carries
 * legacy boolean and 'true'/'false' shapes that must stay positive.
 * AI-CONTEXT: See docs/pitfalls.md#database-string-enum-boolean-coercion
 * AI-CONTEXT: See docs/pitfalls.md#database-rowtitle-join-strands-renamed-rows
 *
 * Mirror of functions/data/matrixRowLookup.js — keep in sync.
 * Callers pass rows they have already soft-delete filtered; this module does
 * not read Firestore and does not filter.
 */

/**
 * @typedef {object} RowDef
 * @property {'negative'|'positive'} posNeg - Canonical valence
 * @property {string} key - Stable bucket key for this row
 * @property {string} ID - MatrixQuestionRows.ID ('' when the doc predates it)
 * @property {string} Title - MatrixQuestionRows.Title
 *
 * @typedef {{byId: Object<string, RowDef>, byTitle: Object<string, RowDef>}} RowLookup
 */

/**
 * Bucket key for a row: its id when present, else its title. Both indexes point
 * at the SAME def, so a row answered before and after RowID existed collapses
 * onto one key instead of splitting into two half-series.
 *
 * @param {{ID?: string, Title?: string}} row
 * @returns {string}
 */
const rowKeyOf = (row) => {
    if (row && row.ID) return `row:${row.ID}`
    if (row && row.Title) return `title:${row.Title}`
    return ''
}

/**
 * Index one scope's worth of rows by id AND title.
 *
 * @param {Array<object>} rowDocs - MatrixQuestionRows doc data
 * @returns {RowLookup}
 */
const buildRowLookup = (rowDocs) => {
    /** @type {RowLookup} */
    const lookup = { byId: {}, byTitle: {} }

    ;(Array.isArray(rowDocs) ? rowDocs : []).forEach((row) => {
        if (!row) return
        const key = rowKeyOf(row)
        if (!key) return

        /** @type {RowDef} */
        const def = {
            posNeg: row.posNeg === 'negative' ? 'negative' : 'positive',
            key,
            ID: row.ID || '',
            Title: row.Title || '',
        }

        if (row.ID) lookup.byId[row.ID] = def
        if (row.Title) lookup.byTitle[row.Title] = def
    })

    return lookup
}

/**
 * Index rows per field. Different fields (typically different organisations)
 * can carry the same row title with opposite valences, so a single global map
 * silently flips negative rows for some of them — always scope by field when
 * the records span more than one.
 *
 * @param {Array<object>} rowDocs - MatrixQuestionRows doc data (each needs `Field`)
 * @returns {Object<string, RowLookup>} keyed by QuestionnaireFields.ID
 */
const buildRowLookupByField = (rowDocs) => {
    /** @type {Object<string, Array<object>>} */
    const grouped = {}

    ;(Array.isArray(rowDocs) ? rowDocs : []).forEach((row) => {
        if (!row || !row.Field) return
        ;(grouped[row.Field] || (grouped[row.Field] = [])).push(row)
    })

    /** @type {Object<string, RowLookup>} */
    const byField = {}
    Object.keys(grouped).forEach((fieldId) => {
        byField[fieldId] = buildRowLookup(grouped[fieldId])
    })

    return byField
}

/**
 * Resolve a response record to its row def. RowID first, RowTitle as the
 * legacy fallback.
 *
 * @param {RowLookup|null|undefined} lookup
 * @param {{RowID?: string, RowTitle?: string}|null|undefined} record
 * @returns {RowDef|null}
 */
const resolveRow = (lookup, record) => {
    if (!lookup || !record) return null
    if (record.RowID && lookup.byId[record.RowID]) return lookup.byId[record.RowID]
    if (record.RowTitle && lookup.byTitle[record.RowTitle]) return lookup.byTitle[record.RowTitle]
    return null
}

/**
 * Whether this record's row is negatively worded. An unresolvable row is never
 * treated as negative — mirroring an unknown row would be a guess.
 *
 * @param {RowLookup|null|undefined} lookup
 * @param {{RowID?: string, RowTitle?: string}|null|undefined} record
 * @returns {boolean}
 */
const isNegativeRow = (lookup, record) => {
    const def = resolveRow(lookup, record)
    return !!def && def.posNeg === 'negative'
}

/**
 * Stable bucket key for a record's row. Unresolvable records get a key of their
 * own rather than merging with a same-titled row from elsewhere.
 *
 * @param {RowLookup|null|undefined} lookup
 * @param {{RowID?: string, RowTitle?: string}|null|undefined} record
 * @returns {string}
 */
const rowKeyFor = (lookup, record) => {
    const def = resolveRow(lookup, record)
    if (def) return def.key
    if (record && record.RowID) return `row:${record.RowID}`
    if (record && record.RowTitle) return `title:${record.RowTitle}`
    return ''
}

export { buildRowLookup, buildRowLookupByField, resolveRow, isNegativeRow, rowKeyFor }
